import type { ReactNode } from "react";
import { ToggleGroup, ToggleGroupItem } from "../ui/toggle-group";
import { useRoomController, useRoomState } from "../../room/RoomContext";
import type { VideoSettings, MicrophoneSettings } from "../../room/media-settings";
import { estimateScreenResourceDemand } from "../../room/media/resource-demand.ts";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldTitle,
} from "../ui/field";
import { Badge } from "../ui/badge";
import { LevelMeter } from "../ui/level-meter";
import { useTranslation } from "react-i18next";

type SegmentOption = { value: string; label: string; nativeLabel: string };

export function ConfigSection({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  // ShadCN's FieldSet/FieldLegend pattern: the legend sits a size above field
  // labels, so section titles don't compete with them.
  return (
    <FieldSet className="@container/field-group has-[[role=switch]]:gap-0">
      <FieldLegend>{title}</FieldLegend>
      <FieldDescription>{description}</FieldDescription>
      {children}
    </FieldSet>
  );
}

export function SegmentedSetting({
  title,
  label,
  target,
  options,
}: {
  title: string;
  label: string;
  target: keyof VideoSettings;
  options: SegmentOption[];
}) {
  const controller = useRoomController();
  const value = useRoomState((s) => s.preferences[target]);
  // Radix uses an empty string for deselection; use a distinct value for Auto.
  const auto = "automatic";
  return (
    <Field orientation="responsive" className="min-w-0">
      <FieldLabel id={`${target}Label`}>{title}</FieldLabel>
      <ToggleGroup
        id={target}
        size="sm"
        type="single"
        value={value || auto}
        aria-label={label}
        className="w-full min-w-0 flex-wrap"
        onValueChange={(next) => {
          if (next) controller.setVideoSetting(target, next === auto ? "" : next);
        }}
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.value}
            value={option.value || auto}
            title={option.nativeLabel}
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </Field>
  );
}

export function ToggleSetting({
  id,
  title,
  description,
}: {
  id: Exclude<keyof MicrophoneSettings, "microphoneDevice">;
  title: string;
  description: string;
}) {
  const controller = useRoomController();
  const checked = useRoomState((s) => s.preferences[id]);
  return (
    <Field orientation="horizontal">
      <FieldContent>
        <FieldLabel htmlFor={id}>{title}</FieldLabel>
        <FieldDescription id={`${id}Description`}>{description}</FieldDescription>
      </FieldContent>
      <Switch
        id={id}
        checked={checked}
        aria-describedby={`${id}Description`}
        onCheckedChange={(value) => controller.setMicrophoneSetting(id, value)}
      />
    </Field>
  );
}

export function MicrophoneDeviceSetting() {
  const { t } = useTranslation();
  const controller = useRoomController();
  const devices = useRoomState((s) => s.media.microphoneDevices);
  const selected = useRoomState((s) => s.preferences.microphoneDevice);
  const defaultDevice = "system-default";
  return (
    <Field orientation="responsive" className="min-w-0">
      <FieldLabel htmlFor="microphoneDevice">{t("voice.inputDevice")}</FieldLabel>
      <Select
        value={selected || defaultDevice}
        onValueChange={(value) =>
          controller.setMicrophoneSetting("microphoneDevice", value === defaultDevice ? "" : value)
        }
      >
        <SelectTrigger id="microphoneDevice">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-w-[calc(100vw-1rem)]">
          <SelectItem value={defaultDevice}>{t("voice.defaultDevice")}</SelectItem>
          {devices.map((device) => (
            <SelectItem
              key={device.deviceId}
              value={device.deviceId}
              className="wrap-anywhere whitespace-normal"
            >
              {device.label}
              {device.isDefault ? (
                <Badge variant="outline" className="ml-2">
                  {t("voice.defaultBadge")}
                </Badge>
              ) : null}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </Field>
  );
}

export function ResourceDemandIndicator() {
  const { t } = useTranslation();
  const resolution = useRoomState((s) => s.preferences.screenResolution);
  const fps = useRoomState((s) => s.preferences.screenFps);
  const contentHint = useRoomState((s) => s.preferences.contentHint);
  const nativeHeight = useRoomState((s) => s.media.screenNativeHeight);
  const demand = estimateScreenResourceDemand({ resolution, fps, contentHint, nativeHeight });
  const labelKey = `settings.resourceDemand${demand.severity[0].toUpperCase()}${demand.severity.slice(1)}`;
  const label = t(labelKey);
  return (
    <Field id="screenResourceDemand" data-level={demand.level}>
      <div className="flex items-center justify-between gap-3">
        <FieldTitle>{t("settings.resourceDemand")}</FieldTitle>
        <Badge variant="secondary">{label}</Badge>
      </div>
      <LevelMeter
        level={demand.level}
        aria-label={t("settings.resourceDemand")}
        aria-valuemin={1}
        aria-valuemax={4}
        aria-valuetext={label}
      />
      <FieldDescription>
        {t(`${labelKey}Description`, {
          resolution: resolution === "native" ? t("common.native") : `${resolution}p`,
          fps,
        })}
      </FieldDescription>
    </Field>
  );
}
